/**
 * Control System Message: one message from the robotics control system
 */
import * as zod from 'zod';

import {
  ArmJointTorquesNullable,
  JointNumber,
  NullableArmJointPositions,
  NullableArmJointVelocities,
} from '@sb/motion-planning';
import { sixteen, two } from '@sb/utilities';

export const MotionStateChangeEvent = zod.object({
  kind: zod.literal('motionStateChange'),
  motionStateChange: zod.object({
    isMoving: zod.boolean(),
  }),
});

export type MotionStateChangeEvent = zod.infer<typeof MotionStateChangeEvent>;

export const CollisionEvent = zod.object({
  kind: zod.literal('collision'),
  collision: zod.object({
    joint: JointNumber,
    disturbanceDetected: zod.number(),
    disturbanceThreshold: zod.number(),
    expectedTorque: zod.number(),
    amperageTorque: zod.number(),
    windupTorque: zod.number(),
    disturbances: ArmJointTorquesNullable,
    positions: NullableArmJointPositions,
    velocities: NullableArmJointVelocities,
  }),
});

export type CollisionEvent = zod.infer<typeof CollisionEvent>;

/**
 * IMU-detected acceleration collision (dual-mode collision detection).
 * Recoverable like a torque collision: routine-runner pauses the routine and
 * arm-control-bot reports RobotStatus::CollisionStop so the scaled JTC holds
 * the trajectory goal for resume (SUP-4071).
 */
export const ImuCollisionEvent = zod.object({
  kind: zod.literal('imuCollision'),
  imuCollision: zod.object({
    // Wire joint index from arm-control-bot's jointNameToIndex(). IMU collisions
    // come from the six motor joints (0..5) AND the end-effector board, which
    // has its own IMU and serializes as JointName::EndEffector1 = 10. It must
    // therefore accept 10 — narrowing to JointNumber (0..5) makes the whole
    // RobotStateUpdate fail to parse on an EE collision, so botman drops the
    // entire state tick and the pause/red-LED flow never fires (SUP-4071).
    // Deliberately NOT a blanket 0..15: the other JointName indices (boards,
    // wheels) have no IMU, so a value outside {0..5, 10} is a genuine error we
    // want to surface loudly rather than accept silently.
    joint: zod.union([JointNumber, zod.literal(10)]),
    /** Acceleration threshold (m/s^2) whose crossing triggered the detection */
    threshold: zod.number(),
    positions: NullableArmJointPositions,
    velocities: NullableArmJointVelocities,
  }),
});

export type ImuCollisionEvent = zod.infer<typeof ImuCollisionEvent>;

/**
 * Matches the wording of the CRITICAL General event message IMU collisions
 * were raised with before becoming a first-class kind, so the user-facing
 * failure text is unchanged.
 */
export function formatImuCollisionMessage(event: ImuCollisionEvent): string {
  return `IMU acceleration collision detected with threshold crossing ${event.imuCollision.threshold}m/s^2 on joint: ${event.imuCollision.joint}`;
}

export const EStopSourceEnum = zod.enum([
  'UI',
  'PUBLIC_API',
  'HARDWARE',
  'UNKNOWN',
]);

export const EStopTriggeredEvent = zod.object({
  kind: zod.literal('eStopTriggered'),
  eStopTriggered: zod.object({
    source: EStopSourceEnum,
    reason: zod.string(),
  }),
});

export type EStopTriggeredEvent = zod.infer<typeof EStopTriggeredEvent>;

export type EStopSource = zod.infer<typeof EStopSourceEnum>;

/**
 * E-Stop sources that represent a deliberate, user-requested power-off
 * rather than an emergency.
 */
export const USER_REQUESTED_ESTOP_SOURCES: readonly EStopSource[] = [
  'UI',
  'PUBLIC_API',
];

/**
 * The reason string the firmware reports back when it executes a
 * software-commanded power-off (arm-control-bot's SafetyManager maps the
 * firmware's EStopReason::ManualEstopTriggered to this text). Stamped with
 * source HARDWARE because it arrives on the firmware CAN stream, but it is
 * the power-off being carried out — not an emergency stop.
 */
export const FIRMWARE_POWER_OFF_REASON = 'manual software command';

/**
 * Whether a stop signal represents a deliberate power-off rather than an
 * emergency: either directly user-requested (UI / public API), or the
 * firmware carrying out the commanded power-off (which reaches us as
 * HARDWARE + FIRMWARE_POWER_OFF_REASON since arm-control-bot stamps
 * everything on the firmware CAN stream as HARDWARE).
 *
 * A genuine emergency is never masked by this mapping: internal safety trips
 * (e.g. encoder drift) stamp their own EStopTriggered failure before their
 * commanded stop echoes back from the firmware, and PoweredOff never
 * replaces an active EStopTriggered failure.
 */
export function isDeliberatePowerOff(
  source: EStopSource,
  reason: string,
): boolean {
  return (
    USER_REQUESTED_ESTOP_SOURCES.includes(source) ||
    (source === 'HARDWARE' && reason === FIRMWARE_POWER_OFF_REASON)
  );
}

export function formatEStopMessage(
  source: EStopSource,
  reason: string,
): string {
  if (!reason) {
    return `E-Stop triggered by ${source}`;
  }

  return `E-Stop triggered by ${source}: ${reason}`;
}

export const SafetyWatchdogViolation = zod.object({
  kind: zod.literal('safetyWatchdogViolation'),
  safetyWatchdogViolation: zod.object({
    violationType: zod.enum(['Position', 'Velocity', 'Torque']),
    violationLevel: zod.enum([
      'USER_SET_LIMIT',
      'ARM_CONTROL_BOT_LIMIT',
      'SAFETY_FAST_LIMIT',
      'SAFETY_SLOW_LIMIT',
    ]),
    actual: zod.number(),
    limit: zod.number(),
    joint: JointNumber,
  }),
});

export type SafetyWatchdogViolation = zod.infer<typeof SafetyWatchdogViolation>;

export const PowerBoardUnhealthyEvent = zod.object({
  kind: zod.literal('powerBoardUnhealthy'),
  powerBoardUnhealthy: zod.object({
    reason: zod.string(),
  }),
});

export type PowerBoardUnhealthyEvent = zod.infer<
  typeof PowerBoardUnhealthyEvent
>;

/**
 * Emitted when the state of a button changes
 */
export const ButtonStateChangeEvent = zod.object({
  kind: zod.literal('buttonStateChangeEvent'),
  buttonStateChangeEvent: zod.object({
    buttonLocation: zod.enum(['controlBox', 'wrist']),
    buttonIndex: zod.number(),
    newButtonState: zod.number(),
  }),
});

export type ButtonStateChangeEvent = zod.infer<typeof ButtonStateChangeEvent>;

// not yet appropriately emitted by followMotionPlan
export const OutOfLimitsEvent = zod.object({
  kind: zod.literal('outOfLimits'),
  outOfLimits: zod.object({
    joint: JointNumber,
    position: zod.number(),
    lowLimit: zod.number(),
    highLimit: zod.number(),
  }),
});

export type OutOfLimitsEvent = zod.infer<typeof OutOfLimitsEvent>;

export const IOState = zod.object({
  eStops: zod.tuple(two(zod.boolean())),
  robotToExternalIO: zod.tuple(sixteen(zod.boolean())),
  externalToRobotIO: zod.tuple(sixteen(zod.boolean())),
  safeguardState: zod.union([
    zod.literal('fullSpeed'),
    zod.literal('slowSpeed'),
    zod.literal('eStop'),
  ]),
});

export type IOState = zod.infer<typeof IOState>;

export const IOStateChanged = zod.object({
  kind: zod.literal('ioStateChanged'),
  ioStateChanged: zod.object({
    oldState: IOState.nullable(),
    newState: IOState.nullable(),
    changes: zod.array(zod.string()),
  }),
});

export type IOStateChanged = zod.infer<typeof IOStateChanged>;

export const BotmanHeartbeatLostEvent = zod.object({
  kind: zod.literal('botmanHeartbeatLost'),
  botmanHeartbeatLost: zod.object({}),
});

export type BotmanHeartbeatLostEvent = zod.infer<
  typeof BotmanHeartbeatLostEvent
>;

export const BotmanHeartbeatRegainedEvent = zod.object({
  kind: zod.literal('botmanHeartbeatRegained'),
  botmanHeartbeatRegained: zod.object({}),
});

export type BotmanHeartbeatRegainedEvent = zod.infer<
  typeof BotmanHeartbeatRegainedEvent
>;

// catchall for as-yet uncategorized control system events
export const GeneralControlSystemEvent = zod.object({
  kind: zod.literal('controlSystemEvent'),
  controlSystemEvent: zod.object({
    message: zod.string(),
    level: zod.enum(['INFO', 'WARNING', 'VIOLATION', 'ERROR', 'CRITICAL']),
    positions: NullableArmJointPositions,
    velocities: NullableArmJointVelocities,
  }),
});

export type GeneralControlSystemEvent = zod.infer<
  typeof GeneralControlSystemEvent
>;

export const ControlSystemEvent = zod
  .union([
    MotionStateChangeEvent,
    CollisionEvent,
    ImuCollisionEvent,
    EStopTriggeredEvent,
    SafetyWatchdogViolation,
    ButtonStateChangeEvent,
    GeneralControlSystemEvent,
    OutOfLimitsEvent,
    IOStateChanged,
    BotmanHeartbeatLostEvent,
    BotmanHeartbeatRegainedEvent,
    PowerBoardUnhealthyEvent,
  ])
  .and(
    zod.object({
      // ISO-8601
      time: zod.string(),
    }),
  );

export type ControlSystemEvent = zod.infer<typeof ControlSystemEvent>;
